import type { LucideIcon } from 'lucide-react';

import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

export type TonoKpi = 'brand' | 'accent' | 'ink' | 'emerald' | 'amber' | 'rose';

const TONOS: Record<TonoKpi, { icon: string; ring: string }> = {
  brand: { icon: 'bg-brand-50 text-brand-600', ring: 'ring-brand-100' },
  accent: { icon: 'bg-accent-50 text-accent-600', ring: 'ring-accent-100' },
  ink: { icon: 'bg-ink-100 text-ink-600', ring: 'ring-ink-200' },
  emerald: { icon: 'bg-emerald-50 text-emerald-600', ring: 'ring-emerald-100' },
  amber: { icon: 'bg-amber-50 text-amber-600', ring: 'ring-amber-100' },
  rose: { icon: 'bg-rose-50 text-rose-600', ring: 'ring-rose-100' },
};

interface KpiAutoservicioProps {
  label: string;
  value: string;
  hint?: string;
  icon: LucideIcon;
  tono?: TonoKpi;
  cargando?: boolean;
}

/**
 * Indicador del dashboard de autoservicio.
 *
 * Los montos nunca van en tipografía grande (convención del panel): el valor
 * usa el mismo tamaño que el resto de KPIs y se trunca con `title` para no
 * romper la maquetación con importes largos.
 */
export function KpiAutoservicio({
  label,
  value,
  hint,
  icon: Icon,
  tono = 'brand',
  cargando = false,
}: KpiAutoservicioProps) {
  const paleta = TONOS[tono];

  return (
    <div className="rounded-card border border-ink-100 bg-white p-4 shadow-soft transition-shadow hover:shadow-card">
      <div className="flex items-start justify-between gap-3">
        <p className="text-sm font-medium text-ink-500">{label}</p>

        <span
          className={cn(
            'flex size-8 shrink-0 items-center justify-center rounded-xl ring-1',
            paleta.icon,
            paleta.ring,
          )}
        >
          <Icon className="size-4" aria-hidden="true" />
        </span>
      </div>

      <div className="mt-2 min-w-0">
        {cargando ? (
          <Skeleton className="h-6 w-24" />
        ) : (
          <p
            title={value}
            className="truncate font-mono text-lg font-bold tracking-tight text-ink-900 tabular-nums"
          >
            {value}
          </p>
        )}
      </div>

      {hint ? <p className="mt-1 truncate text-xs text-ink-400" title={hint}>{hint}</p> : null}
    </div>
  );
}
